import { Injectable, NgZone } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { tap } from 'rxjs';
import { UserMedicineRoutes } from 'src/app/core/enums/routes.enum';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { IMedicineStock } from '../models/medicine-stock.model';
import {
  IApiResponseGroupMedicines,
  IMedicineGroupData,
  IMedicineResponse,
} from '../models/medicine.model';
import { MedicineApiService } from '../services/medicine-api.service';
import {
  AddToStock,
  AdjustStock,
  ClearMedicinesByMenu,
  CreateMedicine,
  DeleteMedicine,
  LoadAllMedicines,
  LoadMedicineById,
  LoadMedicineStock,
  LoadPagedMedicines,
  PrintMedicineReport,
  UpdateMedicine,
  UseMedicine,
  ClearCurrentMedicine,
} from './medicine.actions';

export interface MedicineStateModel {
  medicines: IMedicineResponse[];
  medicinesGrouped: IMedicineGroupData[];
  allMedicines: IMedicineResponse[];
  allMedicinesPerDepartment: IdName[];
  medicinesTotalPages: number;
  currentMedicine: IMedicineResponse | undefined;
  medicineStock: IMedicineStock[];
  medicineReport: Blob;
}

const MEDICINE_STATE_TOKEN = new StateToken<MedicineStateModel>('medicines');

@State<MedicineStateModel>({
  name: MEDICINE_STATE_TOKEN,
  defaults: {
    medicines: [],
    medicinesGrouped: [],
    allMedicines: [],
    allMedicinesPerDepartment: [],
    medicinesTotalPages: 0,
    currentMedicine: undefined,
    medicineStock: [],
    medicineReport: <Blob>{},
  },
})
@Injectable()
export class MedicineState {
  constructor(
    private service: MedicineApiService,
    private zone: NgZone,
    private store: Store
  ) {}

  @Action(LoadPagedMedicines, { cancelUncompleted: true })
  loadPagedMedicines(
    { getState, setState }: StateContext<MedicineStateModel>,
    { filter, menuId }: LoadPagedMedicines
  ) {
    return this.service.getPagedMedicines(filter, menuId).pipe(
      tap((response: IApiResponseGroupMedicines) => {
        const state = getState();
        setState({
          ...state,
          medicines: response.data,
          medicinesGrouped: response.groupData,
          medicinesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllMedicines, { cancelUncompleted: true })
  loadAllMedicines({ getState, setState }: StateContext<MedicineStateModel>) {
    return this.service.getAllMedicines().pipe(
      tap((response: IMedicineResponse[]) => {
        const state = getState();
        setState({
          ...state,
          allMedicines: response,
        });
      })
    );
  }

  @Action(LoadMedicineById, { cancelUncompleted: true })
  loadMedicineById(
    { getState, setState }: StateContext<MedicineStateModel>,
    { id }: LoadMedicineById
  ) {
    return this.service.getMedicineById(id).pipe(
      tap((response: IMedicineResponse) => {
        const state = getState();
        setState({
          ...state,
          currentMedicine: response,
        });
      })
    );
  }

  @Action(CreateMedicine, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineCreatedToast' })
  createMedicine(ctx: StateContext<MedicineStateModel>, { medicine }: CreateMedicine) {
    return this.service.createMedicine(medicine).pipe(
      tap(() => {
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ?? medicine.menuId;
        redirectAndBypassUnsavedDataCheck(`${UserMedicineRoutes.List}/${menuId}`);
      })
    );
  }

  @Action(UpdateMedicine, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineUpdatedToast' })
  updateMedicine(ctx: StateContext<MedicineStateModel>, { medicine }: UpdateMedicine) {
    return this.service.updateMedicine(medicine).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(`${UserMedicineRoutes.Overview}/${medicine.id}`);
      })
    );
  }

  @Action(DeleteMedicine, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineDeletedToast', dispatch: ResetCurrentFilter })
  deleteMedicine(ctx: StateContext<MedicineStateModel>, { medicineId }: DeleteMedicine) {
    const menuId = ctx.getState().currentMedicine?.menu.id;
    return this.service.deleteMedicine(medicineId).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(`${UserMedicineRoutes.List}/${menuId}`);
      })
    );
  }

  @Action(UseMedicine, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineUsed' })
  useMedicine(ctx: StateContext<MedicineStateModel>, { medicine }: UseMedicine) {
    return this.service.useMedicine(medicine).pipe(
      tap(() => {
        ctx.setState({
          ...ctx.getState(),
          medicines: ctx.getState().medicines.map((m) => {
            if (m.id === medicine.id) {
              return { ...m, currentQuantity: m.currentQuantity - medicine.amount };
            }
            return m;
          }),
          medicinesGrouped: ctx.getState().medicinesGrouped.map((group) => {
            return {
              ...group,
              itemsOnNode: group.itemsOnNode.map((m) => {
                if (m.id === medicine.id) {
                  return { ...m, currentQuantity: +m.currentQuantity - +medicine.amount };
                }
                return m;
              }),
            };
          }),
        });
        this.zone.run(() => {
          ctx.dispatch(new LoadMedicineById(medicine.id));
          ctx.dispatch(new LoadMedicineStock(medicine.id));
        });
      })
    );
  }

  @Action(AddToStock, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineAddedToStock' })
  addToStock(ctx: StateContext<MedicineStateModel>, { medicine, redirect }: AddToStock) {
    return this.service.addtoStock(medicine).pipe(
      tap(() => {
        if (redirect) {
          redirectAndBypassUnsavedDataCheck(`${UserMedicineRoutes.Overview}/${medicine.id}`);
        }
        ctx.setState({
          ...ctx.getState(),
          medicinesGrouped: ctx.getState().medicinesGrouped.map((group) => {
            return {
              ...group,
              itemsOnNode: group.itemsOnNode.map((m) => {
                if (m.id === medicine.id) {
                  return { ...m, currentQuantity: +m.currentQuantity + +medicine.amount };
                }
                return m;
              }),
            };
          }),
        });
      })
    );
  }

  @Action(AdjustStock, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineStockAdjusted' })
  adjustStock(ctx: StateContext<MedicineStateModel>, { medicine }: AdjustStock) {
    return this.service.adjustStock(medicine).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(`${UserMedicineRoutes.Overview}/${medicine.id}`);
      })
    );
  }

  @Action(LoadMedicineStock, { cancelUncompleted: true })
  getMedicineStock(
    { getState, setState }: StateContext<MedicineStateModel>,
    { medicineId }: LoadMedicineStock
  ) {
    return this.service.getMedicineStock(medicineId).pipe(
      tap((response: IMedicineStock[]) => {
        const state = getState();
        setState({
          ...state,
          medicineStock: response,
        });
      })
    );
  }

  @Action(PrintMedicineReport, { cancelUncompleted: true })
  printMedicineReport(
    { getState, setState }: StateContext<MedicineStateModel>,
    { filter, menuId }: PrintMedicineReport
  ) {
    return this.service.printReport(filter, menuId).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          medicineReport: file,
        });
      })
    );
  }

  @Action(ClearMedicinesByMenu, { cancelUncompleted: true })
  clearMedicinesByMenu(ctx: StateContext<MedicineStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      medicines: [],
      medicinesGrouped: [],
      medicinesTotalPages: 0,
    });
  }

  @Action(ClearCurrentMedicine)
  clearCurrentMedicine(ctx: StateContext<MedicineStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentMedicine: undefined,
    });
  }

  @Selector()
  static getMedicinesTotalPages(state: MedicineStateModel) {
    return state.medicinesTotalPages;
  }

  @Selector()
  static getMedicineById(state: MedicineStateModel): IMedicineResponse | undefined {
    return state.currentMedicine;
  }

  @Selector()
  static getMedicines(state: MedicineStateModel): IMedicineResponse[] {
    return state.medicines;
  }

  @Selector()
  static getMedicinesGrouped(state: MedicineStateModel): IMedicineGroupData[] {
    return state.medicinesGrouped;
  }

  @Selector()
  static getAllMedicines(state: MedicineStateModel): IMedicineResponse[] {
    return state.allMedicines;
  }

  @Selector()
  static getMedicineStock(state: MedicineStateModel): IMedicineStock[] {
    return state.medicineStock;
  }

  @Selector()
  static getMedicineStockById(state: MedicineStateModel) {
    return (id: string) => {
      return state.medicineStock?.find((medicineStock: IMedicineStock) => medicineStock.id === id);
    };
  }

  @Selector()
  static getMedicineReport(state: MedicineStateModel): Blob {
    return state.medicineReport;
  }
}
